import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { useClickOutside } from '../../internals/useClickOutside';
import { useEscapeKey } from '../../internals/useEscapeKey';
import { useId } from '../../internals/useId';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';
import { zIndex } from '../../tokens/zIndex';

import { useNavbarContext } from './NavbarContext';

/* ─────────────────────────── Shared sizing tokens ───────────────────────── */

const PANEL_RADIUS = 'var(--ui-radius-lg, ' + rem(12) + ')';
const PANEL_SHADOW = `0 ${rem(8)} ${rem(32)} rgba(22,22,22,0.12), 0 ${rem(1)} ${rem(4)} rgba(22,22,22,0.04)`;
const PANEL_FONT = `var(--ui-font-sans, 'HK Grotesk', sans-serif)`;
const ITEM_FONT_SIZE = rem(14);
const ITEM_PADDING_V = rem(9);
const ITEM_PADDING_H = rem(12);
const ITEM_GAP = rem(10);
const ITEM_RADIUS = 'var(--ui-radius-md, ' + rem(6) + ')';
const ICON_SIZE = rem(18);
const SECTION_HEADER_SIZE = rem(11);
const GRID_GAP = rem(12);
const BADGE_SIZE = rem(10);
const TRANSITION_FAST = '0.15s ease';

/* ─────────────────────────── Types ──────────────────────────────────────── */

export type MegaMenuTrigger = 'hover' | 'click' | 'both';
export type MegaMenuVariant = 'panel' | 'fullWidth' | 'overlay';

export interface MegaMenuRootProps {
  label: string;
  active?: boolean;
  trigger?: MegaMenuTrigger;
  variant?: MegaMenuVariant;
  semanticId?: string;
  children: React.ReactNode;
}

/* ─────────────────────────── Animations ─────────────────────────────────── */

const panelSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const panelSlideOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(-6px); }
`;

const overlayFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const overlayFadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

/* ─────────────────────────── Root styled ─────────────────────────────────── */

const TRANSIENT_TRIGGER = ['$active', '$isOpen'] as const;
const shouldForwardTrigger = (prop: string | number): boolean =>
  !TRANSIENT_TRIGGER.includes(prop as (typeof TRANSIENT_TRIGGER)[number]);

const StyledMegaTriggerWrapper = styled.div.withConfig({
  shouldForwardProp: shouldForwardTrigger,
})<{ $active: boolean; $isOpen: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  height: var(--ui-navbar-h, ${rem(67)});
  padding: 0 ${rem(5)};
  cursor: pointer;

  > button {
    font-weight: 400;
    font-size: ${rem(16)};
    line-height: ${rem(22)};
    color: var(--ui-text, ${palette.Black});
    display: inline-flex;
    align-items: center;
    gap: ${rem(2)};
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;
    height: 100%;
    position: relative;
    transition: color 0.2s linear;
    font-family: inherit;
    padding: 0;

    &::after {
      content: '';
      height: 3px;
      width: 0;
      background: var(--ui-interactive, ${palette.Purple});
      position: absolute;
      left: 0;
      bottom: 0;
      transition: width 0.2s linear;
    }
    &:hover::after { width: 100%; }
  }

  ${({ $active, $isOpen }) =>
    ($active || $isOpen) &&
    css`
      > button {
        color: var(--ui-interactive, ${palette.Purple});
        &::after { width: 100%; }
      }
    `}
`;

const StyledPanelContainer = styled.div<{ $variant: MegaMenuVariant }>`
  position: ${({ $variant }) => ($variant === 'overlay' ? 'fixed' : 'absolute')};
  ${({ $variant }) =>
    $variant === 'overlay'
      ? css`top: 0; left: 0; right: 0; bottom: 0; z-index: ${zIndex.navbarDropdown};`
      : $variant === 'fullWidth'
        ? css`
            top: calc(var(--ui-navbar-h, ${rem(67)}) + ${rem(1)});
            left: 50%; transform: translateX(-50%);
            width: 100vw; max-width: 100vw;
            z-index: ${zIndex.navbarDropdown};
          `
        : css`
            top: calc(var(--ui-navbar-h, ${rem(67)}) + ${rem(6)});
            left: 0;
            z-index: ${zIndex.navbarDropdown};
          `}

  &[data-state='open'] {
    animation: ${({ $variant }) => $variant === 'overlay' ? overlayFadeIn : panelSlideIn}
      ${({ $variant }) => ($variant === 'overlay' ? '300ms' : '220ms')} ease-out forwards;
  }
  &[data-state='closed'] {
    animation: ${({ $variant }) => $variant === 'overlay' ? overlayFadeOut : panelSlideOut}
      150ms ease-in forwards;
  }
`;

/* ─────────────────────────── Root component ─────────────────────────────── */

const HOVER_OPEN_DELAY = 80;
const HOVER_CLOSE_DELAY = 250;

const MegaMenuRoot: React.FC<MegaMenuRootProps> = ({
  label, active = false, trigger = 'hover', variant = 'panel', semanticId, children,
}) => {
  const { openDropdownId, setOpenDropdownId } = useNavbarContext();
  const dropdownId = useId('mega-menu');
  const menuId = useId('mega-menu-panel');
  const triggerId = useId('mega-menu-trigger');

  const isOpen = openDropdownId === dropdownId;
  const setOpen = React.useCallback(
    (open: boolean) => setOpenDropdownId(open ? dropdownId : null),
    [dropdownId, setOpenDropdownId],
  );

  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const hoverOpenTimer = React.useRef<number | undefined>();
  const hoverCloseTimer = React.useRef<number | undefined>();

  const semanticProps = getSemanticAttributes(semanticId);
  const close = React.useCallback(() => setOpen(false), [setOpen]);

  useClickOutside(panelRef, close, isOpen, { ignoredRefs: [wrapperRef] });
  useEscapeKey(() => { close(); triggerRef.current?.focus(); }, isOpen);

  const clearTimers = () => {
    if (hoverOpenTimer.current != null) window.clearTimeout(hoverOpenTimer.current);
    if (hoverCloseTimer.current != null) window.clearTimeout(hoverCloseTimer.current);
  };
  React.useEffect(() => clearTimers, []);

  const openIdRef = React.useRef(openDropdownId);
  openIdRef.current = openDropdownId;

  const handleMouseEnter = () => {
    if (trigger !== 'hover' && trigger !== 'both') return;
    clearTimers();
    const delay = openIdRef.current != null ? 0 : HOVER_OPEN_DELAY;
    hoverOpenTimer.current = window.setTimeout(() => setOpenDropdownId(dropdownId), delay);
  };
  const handleMouseLeave = () => {
    if (trigger !== 'hover' && trigger !== 'both') return;
    clearTimers();
    hoverCloseTimer.current = window.setTimeout(() => {
      if (openIdRef.current === dropdownId) setOpenDropdownId(null);
    }, HOVER_CLOSE_DELAY);
  };
  const handleTriggerClick = () => {
    if (trigger !== 'click' && trigger !== 'both') return;
    setOpen(!isOpen);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter') {
      if (!isOpen) { e.preventDefault(); setOpen(true); }
    }
  };

  return (
    <StyledMegaTriggerWrapper
      ref={wrapperRef} $active={active} $isOpen={isOpen}
      onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown} {...semanticProps}
    >
      <button
        ref={triggerRef} type="button" id={triggerId}
        aria-haspopup="menu" aria-expanded={isOpen} aria-controls={menuId}
        onClick={handleTriggerClick}
      >
        {label}
        <ChevronIcon $open={isOpen} />
      </button>

      {isOpen && (
        <StyledPanelContainer
          ref={panelRef} id={menuId} role="menu" aria-labelledby={triggerId}
          tabIndex={-1} data-state="open" $variant={variant}
          onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
        >
          {children}
        </StyledPanelContainer>
      )}
    </StyledMegaTriggerWrapper>
  );
};

MegaMenuRoot.displayName = 'Navbar.MegaMenu';

/* ─────────────────────────── ChevronIcon ────────────────────────────────── */

const TRANSIENT_CHEVRON = ['$open'] as const;
const shouldForwardChevron = (prop: string | number): boolean =>
  !TRANSIENT_CHEVRON.includes(prop as (typeof TRANSIENT_CHEVRON)[number]);

const StyledChevron = styled.span.withConfig({
  shouldForwardProp: shouldForwardChevron,
})<{ $open: boolean }>`
  font-size: ${rem(18)};
  line-height: 1;
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  display: inline-flex;
  align-items: center;
`;

function ChevronIcon({ $open }: { $open: boolean }) {
  return (
    <StyledChevron className="material-symbols-outlined" $open={$open} aria-hidden>
      keyboard_arrow_down
    </StyledChevron>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LAYOUT PRIMITIVES — styled sub-components for each mega menu pattern.
   Consumers compose these inside <Navbar.MegaMenu> to build any variant.

   All colors reference --ui-* CSS custom properties so they adapt
   automatically when the consumer overrides the theme.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─── Panel (base surface) ───────────────────────────────────────────────── */

export interface MegaMenuPanelProps extends React.HTMLAttributes<HTMLDivElement> {}

const StyledPanel = styled.div`
  background: var(--ui-surface, ${palette.White});
  color: var(--ui-text, ${palette.Black});
  border-radius: ${PANEL_RADIUS};
  box-shadow: ${PANEL_SHADOW};
  font-family: ${PANEL_FONT};
  overflow: hidden;
`;

const MegaMenuPanel = React.forwardRef<HTMLDivElement, MegaMenuPanelProps>(
  function MegaMenuPanel({ children, ...rest }, ref) {
    return (
      <StyledPanel ref={ref} {...rest}>
        {children}
      </StyledPanel>
    );
  },
);
MegaMenuPanel.displayName = 'MegaMenu.Panel';

/* ─── Grid (multi-column or tile grid) ───────────────────────────────────── */

export interface MegaMenuGridProps extends React.HTMLAttributes<HTMLDivElement> {
  columns?: number;
  gap?: string;
}

const StyledGrid = styled.div<{ $columns: number; $gap: string }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  gap: ${({ $gap }) => $gap};
`;

const MegaMenuGrid = React.forwardRef<HTMLDivElement, MegaMenuGridProps>(
  function MegaMenuGrid({ columns = 3, gap = GRID_GAP, children, ...rest }, ref) {
    return (
      <StyledGrid ref={ref} $columns={columns} $gap={gap} {...rest}>
        {children}
      </StyledGrid>
    );
  },
);
MegaMenuGrid.displayName = 'MegaMenu.Grid';

/* ─── Column (vertical section with optional header) ─────────────────────── */

export interface MegaMenuColumnProps extends React.HTMLAttributes<HTMLDivElement> {
  header?: string;
  bordered?: boolean;
}

const StyledColumn = styled.div<{ $bordered: boolean }>`
  padding: 0 ${rem(20)};
  ${({ $bordered }) =>
    $bordered &&
    css`border-left: 1px solid var(--ui-border, ${palette.gray[200]});`}
`;

const StyledColumnHeader = styled.div`
  font-size: ${SECTION_HEADER_SIZE};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ui-interactive, ${palette.Purple});
  margin-bottom: ${rem(14)};
  padding-bottom: ${rem(8)};
  border-bottom: 2px solid var(--ui-interactive-muted, ${palette.purple[200]});
`;

const MegaMenuColumn = React.forwardRef<HTMLDivElement, MegaMenuColumnProps>(
  function MegaMenuColumn({ header, bordered = false, children, ...rest }, ref) {
    return (
      <StyledColumn ref={ref} $bordered={bordered} {...rest}>
        {header && <StyledColumnHeader>{header}</StyledColumnHeader>}
        {children}
      </StyledColumn>
    );
  },
);
MegaMenuColumn.displayName = 'MegaMenu.Column';

/* ─── SectionHeader (label above a group) ────────────────────────────────── */

export interface MegaMenuSectionHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  accentColor?: string;
}

const StyledSectionHeader = styled.div<{ $accent?: string }>`
  font-size: ${SECTION_HEADER_SIZE};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ui-text-subtle, ${palette.SpanishGray});
  margin-bottom: ${rem(10)};
  ${({ $accent }) =>
    $accent &&
    css`
      padding-bottom: ${rem(8)};
      border-bottom: 2px solid ${$accent};
    `}
`;

const MegaMenuSectionHeader = React.forwardRef<HTMLDivElement, MegaMenuSectionHeaderProps>(
  function MegaMenuSectionHeader({ accentColor, children, ...rest }, ref) {
    return (
      <StyledSectionHeader ref={ref} $accent={accentColor} {...rest}>
        {children}
      </StyledSectionHeader>
    );
  },
);
MegaMenuSectionHeader.displayName = 'MegaMenu.SectionHeader';

/* ─── Item (single menu link) ────────────────────────────────────────────── */

export type MegaMenuItemDensity = 'compact' | 'default' | 'comfortable';

export interface MegaMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  disabled?: boolean;
  icon?: string;
  description?: string;
  /** @default 'default' */
  density?: MegaMenuItemDensity;
}

const DENSITY_PADDING: Record<MegaMenuItemDensity, string> = {
  compact: `${rem(6)} ${ITEM_PADDING_H}`,
  default: `${ITEM_PADDING_V} ${ITEM_PADDING_H}`,
  comfortable: `${rem(12)} ${ITEM_PADDING_H}`,
};

const StyledItem = styled.div<{ $density: MegaMenuItemDensity }>`
  font-size: ${ITEM_FONT_SIZE};
  color: var(--ui-text, ${palette.Black});
  padding: ${({ $density }) => DENSITY_PADDING[$density]};
  border-radius: ${ITEM_RADIUS};
  cursor: pointer;
  transition: all ${TRANSITION_FAST};
  display: flex;
  align-items: center;
  gap: ${ITEM_GAP};

  .material-symbols-outlined {
    font-size: ${ICON_SIZE};
    color: var(--ui-text-subtle, ${palette.SpanishGray});
    transition: color ${TRANSITION_FAST};
  }

  &:hover {
    background: var(--ui-interactive-muted, ${palette.purple[50]});
    color: var(--ui-interactive, ${palette.Purple});
    .material-symbols-outlined { color: var(--ui-interactive, ${palette.Purple}); }
  }

  &[data-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const ItemDescription = styled.span`
  font-size: ${rem(12)};
  color: var(--ui-text-subtle, ${palette.SpanishGray});
  line-height: 1.5;
`;

const MegaMenuItem = React.forwardRef<HTMLDivElement, MegaMenuItemProps>(
  function MegaMenuItem({ active, disabled, icon, description, density = 'default', children, ...rest }, ref) {
    return (
      <StyledItem
        ref={ref}
        role="menuitem"
        tabIndex={-1}
        data-active={active || undefined}
        data-disabled={disabled || undefined}
        $density={density}
        {...rest}
      >
        {icon && (
          <span className="material-symbols-outlined" aria-hidden>{icon}</span>
        )}
        <span>{children}</span>
        {description && <ItemDescription>{description}</ItemDescription>}
      </StyledItem>
    );
  },
);
MegaMenuItem.displayName = 'MegaMenu.Item';

/* ─── Tile (card with icon, title, description) ──────────────────────────── */

export interface MegaMenuTileProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: string;
  title: string;
  description?: string;
  bg?: string;
  iconColor?: string;
  borderColor?: string;
}

const StyledTile = styled.div<{ $bg?: string; $borderColor?: string }>`
  background: ${({ $bg }) => $bg ?? `var(--ui-surface-muted, ${palette.CulturedWhite})`};
  border: 1px solid ${({ $borderColor }) => $borderColor ?? `var(--ui-border, ${palette.gray[200]})`};
  border-radius: var(--ui-radius-lg, ${rem(10)});
  padding: ${rem(16)};
  cursor: pointer;
  transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
              box-shadow 0.22s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 ${rem(8)} ${rem(24)} rgba(22, 22, 22, 0.1),
                0 ${rem(4)} ${rem(8)} rgba(22, 22, 22, 0.06);
  }
`;

const TileTitle = styled.div`
  font-size: ${ITEM_FONT_SIZE};
  font-weight: 600;
  color: var(--ui-text, ${palette.Black});
  margin-bottom: ${rem(4)};
`;

const TileDesc = styled.div`
  font-size: ${rem(12)};
  color: var(--ui-text-subtle, ${palette.SpanishGray});
  line-height: 1.5;
`;

const MegaMenuTile = React.forwardRef<HTMLDivElement, MegaMenuTileProps>(
  function MegaMenuTile({ icon, title, description, bg, iconColor, borderColor, children, ...rest }, ref) {
    return (
      <StyledTile ref={ref} $bg={bg} $borderColor={borderColor} {...rest}>
        {icon && (
          <span
            className="material-symbols-outlined"
            style={{
              fontSize: rem(28),
              color: iconColor ?? `var(--ui-interactive, ${palette.Purple})`,
              display: 'block',
              marginBottom: rem(10),
            }}
            aria-hidden
          >
            {icon}
          </span>
        )}
        <TileTitle>{title}</TileTitle>
        {description && <TileDesc>{description}</TileDesc>}
        {children}
      </StyledTile>
    );
  },
);
MegaMenuTile.displayName = 'MegaMenu.Tile';

/* ─── Badge (New / Beta / count label) ───────────────────────────────────── */

export interface MegaMenuBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** @default 'default' */
  variant?: 'default' | 'success' | 'warning' | 'info';
}

const StyledBadge = styled.span<{ $variant: string }>`
  font-size: ${BADGE_SIZE};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: ${rem(2)} ${rem(8)};
  border-radius: var(--ui-radius-full, ${rem(99)});
  line-height: 1.4;

  &[data-variant='default'] {
    background: var(--ui-interactive-muted, ${palette.purple[100]});
    color: var(--ui-interactive, ${palette.Purple});
  }
  &[data-variant='success'] {
    background: ${palette.green[100]};
    color: ${palette.green[800]};
  }
  &[data-variant='warning'] {
    background: ${palette.amber[100]};
    color: ${palette.amber[800]};
  }
  &[data-variant='info'] {
    background: var(--ui-interactive, ${palette.Purple});
    color: ${palette.White};
  }
`;

const MegaMenuBadge = React.forwardRef<HTMLSpanElement, MegaMenuBadgeProps>(
  function MegaMenuBadge({ variant = 'default', children, ...rest }, ref) {
    return (
      <StyledBadge ref={ref} $variant={variant} data-variant={variant} {...rest}>
        {children}
      </StyledBadge>
    );
  },
);
MegaMenuBadge.displayName = 'MegaMenu.Badge';

/* ─── TwoPanel (sidebar flyout — left sidebar, right detail) ─────────────── */

export interface MegaMenuTwoPanelProps extends React.HTMLAttributes<HTMLDivElement> {}

const StyledTwoPanel = styled.div`
  display: flex;
  min-width: ${rem(680)};
  max-height: ${rem(420)};
  border-radius: ${PANEL_RADIUS};
  overflow: hidden;
  box-shadow: ${PANEL_SHADOW};
  font-family: ${PANEL_FONT};
`;

const MegaMenuTwoPanel = React.forwardRef<HTMLDivElement, MegaMenuTwoPanelProps>(
  function MegaMenuTwoPanel({ children, ...rest }, ref) {
    return <StyledTwoPanel ref={ref} {...rest}>{children}</StyledTwoPanel>;
  },
);
MegaMenuTwoPanel.displayName = 'MegaMenu.TwoPanel';

/* ─── TwoPanel.Sidebar ───────────────────────────────────────────────────── */

export interface MegaMenuSidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  width?: string;
}

const StyledSidebar = styled.div<{ $width: string }>`
  width: ${({ $width }) => $width};
  background: var(--ui-surface-muted, ${palette.CulturedWhite});
  padding: ${rem(16)} 0;
  flex-shrink: 0;
`;

const MegaMenuSidebar = React.forwardRef<HTMLDivElement, MegaMenuSidebarProps>(
  function MegaMenuSidebar({ width = rem(220), children, ...rest }, ref) {
    return <StyledSidebar ref={ref} $width={width} {...rest}>{children}</StyledSidebar>;
  },
);
MegaMenuSidebar.displayName = 'MegaMenu.Sidebar';

/* ─── TwoPanel.Detail ────────────────────────────────────────────────────── */

export interface MegaMenuDetailProps extends React.HTMLAttributes<HTMLDivElement> {}

const StyledDetail = styled.div`
  flex: 1;
  background: var(--ui-surface, ${palette.White});
  padding: ${rem(24)} ${rem(28)};
  overflow-y: auto;
`;

const MegaMenuDetail = React.forwardRef<HTMLDivElement, MegaMenuDetailProps>(
  function MegaMenuDetail({ children, ...rest }, ref) {
    return <StyledDetail ref={ref} {...rest}>{children}</StyledDetail>;
  },
);
MegaMenuDetail.displayName = 'MegaMenu.Detail';

/* ─── CategoryItem (sidebar row) ─────────────────────────────────────────── */

export interface MegaMenuCategoryItemProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  icon?: string;
}

const TRANSIENT_CAT = ['$active'] as const;
const shouldForwardCat = (prop: string | number): boolean =>
  !TRANSIENT_CAT.includes(prop as (typeof TRANSIENT_CAT)[number]);

const StyledCategoryItem = styled.div.withConfig({ shouldForwardProp: shouldForwardCat })<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${ITEM_GAP};
  padding: ${rem(11)} ${rem(24)};
  font-size: ${ITEM_FONT_SIZE};
  color: var(--ui-text-subtle, ${palette.SpanishGray});
  cursor: pointer;
  transition: all ${TRANSITION_FAST};
  border-left: 3px solid transparent;

  .material-symbols-outlined {
    font-size: ${rem(20)};
    color: var(--ui-text-subtle, ${palette.SpanishGray});
    transition: color ${TRANSITION_FAST};
  }

  ${({ $active }) =>
    $active &&
    css`
      color: var(--ui-interactive, ${palette.Purple});
      background: var(--ui-interactive-muted, ${palette.purple[50]});
      border-left-color: var(--ui-interactive, ${palette.Purple});
      .material-symbols-outlined { color: var(--ui-interactive, ${palette.Purple}); }
    `}

  &:hover {
    color: var(--ui-interactive, ${palette.Purple});
    background: var(--ui-interactive-muted, ${palette.purple[50]});
    .material-symbols-outlined { color: var(--ui-interactive, ${palette.Purple}); }
  }
`;

const MegaMenuCategoryItem = React.forwardRef<HTMLDivElement, MegaMenuCategoryItemProps>(
  function MegaMenuCategoryItem({ active = false, icon, children, ...rest }, ref) {
    return (
      <StyledCategoryItem ref={ref} $active={active} {...rest}>
        {icon && <span className="material-symbols-outlined" aria-hidden>{icon}</span>}
        {children}
      </StyledCategoryItem>
    );
  },
);
MegaMenuCategoryItem.displayName = 'MegaMenu.CategoryItem';

/* ─── TabStrip + Tab ─────────────────────────────────────────────────────── */

export interface MegaMenuTabStripProps extends React.HTMLAttributes<HTMLDivElement> {}

const StyledTabStrip = styled.div`
  display: flex;
  border-bottom: 2px solid var(--ui-border, ${palette.gray[200]});
  background: var(--ui-surface-muted, ${palette.CulturedWhite});
`;

const MegaMenuTabStrip = React.forwardRef<HTMLDivElement, MegaMenuTabStripProps>(
  function MegaMenuTabStrip({ children, ...rest }, ref) {
    return <StyledTabStrip ref={ref} {...rest}>{children}</StyledTabStrip>;
  },
);
MegaMenuTabStrip.displayName = 'MegaMenu.TabStrip';

export interface MegaMenuTabProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
}

const TRANSIENT_TAB = ['$active'] as const;
const shouldForwardTab = (prop: string | number): boolean =>
  !TRANSIENT_TAB.includes(prop as (typeof TRANSIENT_TAB)[number]);

const StyledTab = styled.button.withConfig({ shouldForwardProp: shouldForwardTab })<{ $active: boolean }>`
  padding: ${rem(12)} ${rem(20)};
  font-size: ${rem(13)};
  font-weight: 600;
  color: var(--ui-text-subtle, ${palette.SpanishGray});
  background: none;
  border: none;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  position: relative;
  font-family: inherit;
  transition: color ${TRANSITION_FAST};
  display: flex;
  align-items: center;
  gap: ${rem(6)};

  &::after {
    content: '';
    position: absolute;
    bottom: -2px; left: 0; right: 0;
    height: 3px;
    background: transparent;
    transition: background ${TRANSITION_FAST};
  }

  ${({ $active }) =>
    $active &&
    css`
      color: var(--ui-interactive, ${palette.Purple});
      &::after { background: var(--ui-interactive, ${palette.Purple}); }
    `}

  &:hover { color: var(--ui-interactive, ${palette.Purple}); }
`;

const MegaMenuTab = React.forwardRef<HTMLButtonElement, MegaMenuTabProps>(
  function MegaMenuTab({ active = false, children, ...rest }, ref) {
    return <StyledTab ref={ref} $active={active} {...rest}>{children}</StyledTab>;
  },
);
MegaMenuTab.displayName = 'MegaMenu.Tab';

/* ─── SearchInput (search bar) ───────────────────────────────────────────── */

export interface MegaMenuSearchInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  leadingIcon?: string;
  trailing?: React.ReactNode;
}

const StyledSearchWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: ${rem(14)} ${rem(16)};
  border-bottom: 1px solid var(--ui-border, ${palette.gray[200]});
  gap: ${ITEM_GAP};

  .material-symbols-outlined {
    font-size: ${rem(20)};
    color: var(--ui-text-subtle, ${palette.SpanishGray});
  }

  input {
    flex: 1;
    background: none;
    border: none;
    outline: none;
    font-size: ${ITEM_FONT_SIZE};
    color: var(--ui-text, ${palette.Black});
    font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
    caret-color: var(--ui-interactive, ${palette.Purple});
    &::placeholder { color: var(--ui-text-subtle, ${palette.SpanishGray}); }
  }
`;

const MegaMenuSearchInput = React.forwardRef<HTMLInputElement, MegaMenuSearchInputProps>(
  function MegaMenuSearchInput({ leadingIcon = 'search', trailing, ...rest }, ref) {
    return (
      <StyledSearchWrapper>
        <span className="material-symbols-outlined" aria-hidden>{leadingIcon}</span>
        <input ref={ref} {...rest} />
        {trailing}
      </StyledSearchWrapper>
    );
  },
);
MegaMenuSearchInput.displayName = 'MegaMenu.SearchInput';

/* ─── Kbd (keyboard shortcut badge) ──────────────────────────────────────── */

export interface MegaMenuKbdProps extends React.HTMLAttributes<HTMLElement> {}

const StyledKbd = styled.kbd`
  font-size: ${rem(11)};
  font-family: 'Menlo', monospace;
  background: var(--ui-surface-muted, ${palette.CulturedWhite});
  color: var(--ui-text-subtle, ${palette.SpanishGray});
  padding: ${rem(2)} ${rem(6)};
  border-radius: var(--ui-radius-sm, ${rem(4)});
  border: 1px solid var(--ui-border, ${palette.gray[200]});
`;

const MegaMenuKbd = React.forwardRef<HTMLElement, MegaMenuKbdProps>(
  function MegaMenuKbd({ children, ...rest }, ref) {
    return <StyledKbd ref={ref} {...rest}>{children}</StyledKbd>;
  },
);
MegaMenuKbd.displayName = 'MegaMenu.Kbd';

/* ─── Featured (promotional card / CTA block) ───────────────────────────── */

export interface MegaMenuFeaturedProps extends React.HTMLAttributes<HTMLDivElement> {
  bg?: string;
}

const StyledFeatured = styled.div<{ $bg?: string }>`
  background: ${({ $bg }) => $bg ?? `var(--ui-interactive-muted, ${palette.purple[50]})`};
  border-radius: var(--ui-radius-md, ${rem(8)});
  padding: ${rem(14)};
  font-size: ${rem(12)};
  color: var(--ui-text-subtle, ${palette.SpanishGray});
  line-height: 1.5;
  margin-top: ${rem(14)};

  strong {
    display: block;
    font-size: ${rem(13)};
    color: var(--ui-interactive, ${palette.Purple});
    margin-bottom: ${rem(4)};
  }
`;

const MegaMenuFeatured = React.forwardRef<HTMLDivElement, MegaMenuFeaturedProps>(
  function MegaMenuFeatured({ bg, children, ...rest }, ref) {
    return <StyledFeatured ref={ref} $bg={bg} {...rest}>{children}</StyledFeatured>;
  },
);
MegaMenuFeatured.displayName = 'MegaMenu.Featured';

/* ═══════════════════════════════════════════════════════════════════════════
   COMPOUND EXPORT
   ═══════════════════════════════════════════════════════════════════════════ */

export const MegaMenu = Object.assign(MegaMenuRoot, {
  Panel: MegaMenuPanel,
  Grid: MegaMenuGrid,
  Column: MegaMenuColumn,
  SectionHeader: MegaMenuSectionHeader,
  Item: MegaMenuItem,
  Tile: MegaMenuTile,
  Badge: MegaMenuBadge,
  TwoPanel: MegaMenuTwoPanel,
  Sidebar: MegaMenuSidebar,
  Detail: MegaMenuDetail,
  CategoryItem: MegaMenuCategoryItem,
  TabStrip: MegaMenuTabStrip,
  Tab: MegaMenuTab,
  SearchInput: MegaMenuSearchInput,
  Kbd: MegaMenuKbd,
  Featured: MegaMenuFeatured,
});
